import React from 'react'
import { Droplets, Wind, Gauge } from 'lucide-react'
import { useWeather } from '../contexts/WeatherContext'
import { formatTemperature, formatDate, getWeatherIcon } from '../utils/formatters'

const WeatherDashboard: React.FC = () => {
  const { forecast, location, isLoading, error } = useWeather()

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center text-red-600 dark:text-red-400">
          <p className="text-lg font-semibold mb-2">Error loading weather data</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  if (!forecast) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <p>No weather data available</p>
        </div>
      </div>
    )
  }

  const { current, hourly, daily } = forecast

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
      {/* Current Weather */}
      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">{location?.display_name}</h2>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-6xl font-bold">
              {formatTemperature(current.temperature_celsius)}
            </div>
            <p className="text-xl capitalize mt-2">{current.condition}</p>
            <p className="text-sm opacity-90">
              Feels like {formatTemperature(current.feels_like_celsius)}
            </p>
          </div>
          <div className="text-7xl">
            {getWeatherIcon(current.condition)}
          </div>
        </div>

        {/* Current Stats */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="flex items-center space-x-2">
            <Droplets size={20} />
            <div>
              <p className="text-sm opacity-90">Humidity</p>
              <p className="font-semibold">{current.humidity_percent}%</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Wind size={20} />
            <div>
              <p className="text-sm opacity-90">Wind</p>
              <p className="font-semibold">{current.wind_speed_ms.toFixed(1)} m/s</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Gauge size={20} />
            <div>
              <p className="text-sm opacity-90">Pressure</p>
              <p className="font-semibold">{current.pressure_hpa.toFixed(0)} hPa</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Droplets size={20} />
            <div>
              <p className="text-sm opacity-90">Precipitation</p>
              <p className="font-semibold">{current.precipitation_mm} mm</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hourly Forecast */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Hourly Forecast
        </h3>
        <div className="overflow-x-auto">
          <div className="flex space-x-4">
            {hourly.slice(0, 12).map((hour, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 text-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700"
              >
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {new Date(hour.timestamp).getHours()}:00
                </p>
                <div className="text-3xl mb-2">{getWeatherIcon(hour.condition)}</div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {formatTemperature(hour.temperature_celsius)}
                </p>
                {hour.precipitation_probability > 0.3 && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {Math.round(hour.precipitation_probability * 100)}%
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Forecast */}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          7-Day Forecast
        </h3>
        <div className="space-y-3">
          {daily.map((day, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700"
            >
              <div className="flex items-center space-x-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white w-20">
                  {formatDate(day.date)}
                </p>
                <div className="text-2xl">{getWeatherIcon(day.condition)}</div>
                <p className="text-sm capitalize text-gray-600 dark:text-gray-400">
                  {day.condition}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                {day.precipitation_probability > 0.3 && (
                  <div className="flex items-center space-x-1 text-blue-600 dark:text-blue-400">
                    <Droplets size={16} />
                    <span className="text-sm">
                      {Math.round(day.precipitation_probability * 100)}%
                    </span>
                  </div>
                )}
                <div className="text-right">
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatTemperature(day.temp_high_celsius)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatTemperature(day.temp_low_celsius)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default WeatherDashboard
